import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Search, Download } from "lucide-react";
import { api, inr, fmtDate, apiError } from "@/lib/api";
import { generateReceiptPdf } from "@/lib/receiptPdf";
import { Skeleton, StatusBadge, EmptyState } from "@/components/common";
import { useT } from "@/context/i18n";

export default function PaymentHistory() {
  const [all, setAll] = useState(null);
  const t = useT();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [loanId, setLoanId] = useState("ALL");
  const [loans, setLoans] = useState([]);

  useEffect(() => {
    api.get("/payments").then((r) => setAll(r.data)).catch(() => setAll([]));
    api.get("/loans").then((r) => setLoans(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!all) return [];
    return all.filter((p) =>
      (status === "ALL" || p.status === status) &&
      (loanId === "ALL" || p.loan_id === loanId) &&
      (!q || (p.txn_id || "").toLowerCase().includes(q.toLowerCase()) || (p.lender || "").toLowerCase().includes(q.toLowerCase()))
    );
  }, [all, q, status, loanId]);

  const receipt = async (txn) => {
    try {
      const { data } = await api.get(`/payments/${txn}/receipt`);
      await generateReceiptPdf(data);
      toast.success("Receipt downloaded");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!all) return <div className="space-y-4"><Skeleton className="h-10 w-full" />{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16" />)}</div>;

  const sel = "rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-[#0B132B] outline-none focus:border-[#14B8A6]";

  const lifecycleBadge = (p) => {
    if (p.status !== "SUCCESS") return <span className="text-xs text-slate-300">—</span>;
    const st = p.payment_state;
    if (!st) return <span className="text-xs text-slate-300">—</span>;
    const map = {
      LENDER_CONFIRMED: ["Lender Confirmed", "bg-emerald-100 text-emerald-700"],
      LENDER_REJECTED: ["Not Confirmed", "bg-red-100 text-red-600"],
      AWAITING_LENDER_CONFIRMATION: ["Awaiting Lender", "bg-amber-100 text-amber-700"],
      LEGACY_UNKNOWN: ["Legacy", "bg-slate-100 text-slate-500"],
    };
    const [label, cls] = map[st] || ["Awaiting Lender", "bg-amber-100 text-amber-700"];
    return <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${cls}`} data-testid={`lifecycle-${p.txn_id}`}>{label}</span>;
  };

  return (
    <div className="space-y-5">
      <div><h1 className="font-display text-2xl font-bold text-[#0B132B]">{t("Payment History")}</h1><p className="text-sm text-slate-500">{t("Search, filter and download receipts for all your payments.")}</p></div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-2.5 text-slate-400" />
          <input data-testid="pay-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Search txn ID or lender")} className="w-full rounded-md border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[#14B8A6]" />
        </div>
        <select data-testid="pay-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={sel}>
          {["ALL", "SUCCESS", "PENDING", "FAILED"].map((s) => <option key={s} value={s}>{s === "ALL" ? t("All statuses") : s}</option>)}
        </select>
        <select data-testid="pay-loan-filter" value={loanId} onChange={(e) => setLoanId(e.target.value)} className={sel}>
          <option value="ALL">{t("All loans")}</option>
          {loans.map((l) => <option key={l.id} value={l.id}>{l.lender} — {l.loan_type}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? <EmptyState icon={CreditCard} title={t("No payments found")} desc={t("Try changing your filters, or make your first EMI payment from the Loans page.")} /> : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm md:block">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>{["Date", "Loan", "Amount", "Status", "Lender", "Txn ID", "Method", ""].map((h) => <th key={h} className="px-4 py-3">{t(h)}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-600">{fmtDate(p.created_at)}</td>
                    <td className="px-4 py-3 font-medium text-[#0B132B]">{p.lender}</td>
                    <td className="px-4 py-3 font-semibold text-[#0B132B]">{inr(p.amount)}</td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3">{lifecycleBadge(p)}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{p.txn_id}</td>
                    <td className="px-4 py-3 text-slate-600">{(p.method || "—").replace("_", " ")}</td>
                    <td className="px-4 py-3">{p.status === "SUCCESS" && <button onClick={() => receipt(p.txn_id)} className="inline-flex items-center gap-1 text-xs font-semibold text-[#14B8A6]"><Download size={13} /> {t("Receipt")}</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {filtered.map((p) => (
              <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between"><p className="font-semibold text-[#0B132B]">{p.lender}</p><StatusBadge status={p.status} /></div>
                <div className="mt-2 flex items-center justify-between text-sm"><span className="text-slate-500">{fmtDate(p.created_at)}</span><span className="font-display font-bold text-[#0B132B]">{inr(p.amount)}</span></div>
                <div className="mt-1 flex items-center justify-between text-xs text-slate-400"><span>{p.txn_id}</span>{p.status === "SUCCESS" && <button onClick={() => receipt(p.txn_id)} className="font-semibold text-[#14B8A6]">{t("Receipt")}</button>}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
